/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import { useCallback, useEffect } from 'react';
/* eslint camelcase: 0 */
import URI from 'urijs';
import {
  buildQueryContext,
  ensureIsArray,
  getChartBuildQueryRegistry,
  getChartMetadataRegistry,
  SupersetClient,
} from '@superset-ui/core';
import { availableDomains } from 'src/utils/hostNamesConfig';
import { safeStringify } from 'src/utils/safeStringify';
import { optionLabel, isArrayColumnType } from 'src/utils/common';
import { URL_PARAMS } from 'src/constants';
import {
  MULTI_OPERATORS,
  OPERATOR_ENUM_TO_OPERATOR_TYPE,
  UNSAVED_CHART_ID,
} from 'src/explore/constants';
import { DashboardStandaloneMode } from 'src/dashboard/util/constants';

export function getChartKey(explore) {
  const { slice, form_data } = explore;
  return slice?.slice_id ?? form_data?.slice_id ?? UNSAVED_CHART_ID;
}

let requestCounter = 0;
export function getHostName(allowDomainSharding = false) {
  let currentIndex = 0;
  if (allowDomainSharding) {
    currentIndex = requestCounter % availableDomains.length;
    requestCounter += 1;

    // if domain sharding is enabled, skip main domain for fetching chart API
    // leave main domain free for other calls like fav star, save change, etc.
    // to make dashboard be responsive when it's loading large number of charts
    if (currentIndex === 0) {
      currentIndex += 1;
      requestCounter += 1;
    }
  }
  return availableDomains[currentIndex];
}

export function getAnnotationJsonUrl(slice_id, force) {
  if (slice_id === null || slice_id === undefined) {
    return null;
  }

  const uri = URI(window.location.search);
  return uri
    .pathname('/api/v1/chart/data')
    .search({
      form_data: safeStringify({ slice_id }),
      force,
    })
    .toString();
}

export function getURIDirectory(endpointType = 'base') {
  // Building the directory part of the URI
  if (
    ['full', 'json', 'csv', 'query', 'results', 'samples'].includes(
      endpointType,
    )
  ) {
    return '/nezha/explore_json/';
  }
  return '/explore/';
}

export function mountExploreUrl(endpointType, extraSearch = {}, force = false) {
  const uri = new URI('/');
  const directory = getURIDirectory(endpointType);
  const search = uri.search(true);
  Object.keys(extraSearch).forEach(key => {
    search[key] = extraSearch[key];
  });
  if (endpointType === URL_PARAMS.standalone.name) {
    if (force) {
      search.force = '1';
    }
    search.standalone = DashboardStandaloneMode.HideNav;
  }
  return uri.directory(directory).search(search).toString();
}

export function getChartDataUri({ path, qs, allowDomainSharding = false }) {
  // The search params from the window.location are carried through,
  // but can be specified with curUrl (used for unit tests to spoof
  // the window.location).
  let uri = new URI({
    protocol: window.location.protocol.slice(0, -1),
    hostname: getHostName(allowDomainSharding),
    port: window.location.port ? window.location.port : '',
    path,
  });
  if (qs) {
    uri = uri.search(qs);
  }
  return uri;
}

/**
 * This gets the minimal url for the given form data.
 * If there are dashboard overrides present in the form data,
 * they will not be included in the url.
 */
export function getExploreUrl({
  formData,
  endpointType = 'base',
  force = false,
  curUrl = null,
  requestParams = {},
  allowDomainSharding = false,
  method = 'POST',
}) {
  if (!formData.datasource) {
    return null;
  }

  // label_colors should not pollute the URL
  // eslint-disable-next-line no-param-reassign
  delete formData.label_colors;

  let uri = getChartDataUri({ path: '/', allowDomainSharding });
  if (curUrl) {
    uri = URI(URI(curUrl).search());
  }

  const directory = getURIDirectory(endpointType);

  // Building the querystring (search) part of the URI
  const search = uri.search(true);
  const { slice_id, extra_filters, adhoc_filters, viz_type } = formData;
  if (slice_id) {
    const form_data = { slice_id };
    if (method === 'GET') {
      form_data.viz_type = viz_type;
      if (extra_filters && extra_filters.length) {
        form_data.extra_filters = extra_filters;
      }
      if (adhoc_filters && adhoc_filters.length) {
        form_data.adhoc_filters = adhoc_filters;
      }
    }
    search.form_data = safeStringify(form_data);
  }
  if (force) {
    search.force = 'true';
  }
  if (endpointType === 'csv') {
    search.csv = 'true';
  }
  if (endpointType === URL_PARAMS.standalone.name) {
    search.standalone = '1';
  }
  if (endpointType === 'query') {
    search.query = 'true';
  }
  if (endpointType === 'results') {
    search.results = 'true';
  }
  if (endpointType === 'samples') {
    search.samples = 'true';
  }
  const paramNames = Object.keys(requestParams);
  if (paramNames.length) {
    paramNames.forEach(name => {
      if (requestParams.hasOwnProperty(name)) {
        search[name] = requestParams[name];
      }
    });
  }
  return uri.search(search).directory(directory).toString();
}

export const getQuerySettings = formData => {
  const vizMetadata = getChartMetadataRegistry().get(formData.viz_type);
  return [
    !formData.isFromSqlLab && vizMetadata ? vizMetadata.useLegacyApi : false,
    vizMetadata?.parseMethod ?? 'json-bigint',
  ];
};

export const getSavedCohorts = formData => {
  const { measurements, adhoc_filters: globalFilters } = formData;
  const savedCohorts = new Set();

  const addCohorts = filter => {
    filter?.savedCohorts?.forEach(cohort => savedCohorts.add(cohort));
  };

  // Add cohorts from measurements
  measurements?.forEach(measure => addCohorts(measure.AdhocFilterControl?.[0]));

  // Add cohorts from global filters
  globalFilters?.forEach(addCohorts);

  return [...savedCohorts];
};

export const buildV1ChartDataPayload = ({
  formData,
  force,
  resultFormat,
  resultType,
  setDataMask,
  ownState,
}) => {
  const buildQuery =
    getChartBuildQueryRegistry().get(formData.viz_type) ??
    (buildQueryformData =>
      buildQueryContext(buildQueryformData, baseQueryObject => [
        {
          ...baseQueryObject,
        },
      ]));
  return buildQuery(
    {
      ...formData,
      force,
      result_format: resultFormat,
      result_type: resultType,
    },
    {
      ownState,
      hooks: {
        setDataMask,
      },
    },
  );
};

export const getLegacyEndpointType = ({ resultType, resultFormat }) =>
  resultFormat === 'csv' ? resultFormat : resultType;

export const exportChart = ({
  formData,
  resultFormat = 'json',
  resultType = 'full',
  force = false,
  ownState = {},
}) => {
  let url;
  let payload;
  const [useLegacyApi, parseMethod] = getQuerySettings(formData);
  if (useLegacyApi) {
    const endpointType = getLegacyEndpointType({ resultFormat, resultType });
    url = getExploreUrl({
      formData,
      endpointType,
      allowDomainSharding: false,
    });
    payload = formData;
  } else {
    url = '/api/v1/chart/data';
    payload = buildV1ChartDataPayload({
      formData,
      force,
      resultFormat,
      resultType,
      ownState,
      parseMethod,
    });
  }

  SupersetClient.postForm(url, { form_data: safeStringify(payload) });
};

export const exploreChart = (formData, requestParams) => {
  const url = getExploreUrl({
    formData,
    endpointType: 'base',
    allowDomainSharding: false,
    requestParams,
  });
  SupersetClient.postForm(url, { form_data: safeStringify(formData) });
};

export const useDebouncedEffect = (effect, delay, deps) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callback = useCallback(effect, deps);

  useEffect(() => {
    const handler = setTimeout(() => {
      callback();
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [callback, delay]);
};

export const getSimpleKQLExpression = (subject, operator, comparator) => {
  const isFuncOperator = ['in', '!in'].indexOf(operator) > -1;
  const isNullOperator = ['isnull', 'isnotnull'].indexOf(operator) > -1;
  const lhs = subject?.column_name ?? subject ?? '';

  // isnull/isnotnull are KQL functions wrapping the column
  if (isNullOperator) {
    return `${operator}(${lhs})`;
  }

  // normalize comparator to an array of values
  const comparatorArray = Array.isArray(comparator) ? comparator : [comparator];

  // unless the comparator value is a number, wrap it in string quotes
  const rhs = comparatorArray
    .map(c => (Number.isNaN(Number(c)) ? `'${c ?? ''}'` : c))
    .join(',');

  const funcOpen = isFuncOperator ? '(' : '';
  const funcClosed = isFuncOperator ? ')' : '';

  return `${lhs} ${operator} ${funcOpen}${rhs}${funcClosed}`;
};

export const getSimpleSQLExpression = (
  subject,
  columnType,
  operator,
  comparator,
  defaultPrefix = '',
  defaultSuffix = '',
  useKql = false,
) => {
  if (useKql) {
    return getSimpleKQLExpression(subject, operator, comparator);
  }
  const isMulti =
    [...MULTI_OPERATORS]
      .map(op => OPERATOR_ENUM_TO_OPERATOR_TYPE[op].operation)
      .indexOf(operator) >= 0;
  let expression =
    typeof subject === 'object'
      ? (subject?.column_name ?? '')
      : (subject ?? '');
  if (subject && operator) {
    const firstValue =
      isMulti && Array.isArray(comparator) ? comparator[0] : comparator;
    const isString =
      firstValue !== undefined &&
      (firstValue => {
        if (
          operator === 'LIKE' ||
          operator === 'ILIKE' ||
          operator === 'NOT LIKE' ||
          operator === 'NOT ILIKE'
        ) {
          return true;
        }
        return (
          Number.isNaN(Number(firstValue)) ||
          ['STRING', 'LOWCARDINALITY(STRING)', 'ARRAY(STRING)'].includes(
            columnType,
          )
        );
      })(firstValue);
    const comparatorArray = ensureIsArray(
      (function (prefix, comparator, suffix) {
        if (
          comparator === null ||
          (comparator === undefined && !Array.isArray(comparator))
        ) {
          return comparator;
        }
        if (Array.isArray(comparator)) {
          return comparator.map(val =>
            (val => {
              if (isString) {
                return `${val}`;
              }
              return val;
            })(val),
          );
        }
        return `${prefix}${comparator}${suffix}`;
      })(defaultPrefix, comparator, defaultSuffix),
    );
    const quote = isString ? "'" : '';
    const formattedComparators = comparatorArray
      .map(val => optionLabel(val))
      .map(val => {
        // Currently dropdowns for array columns are being populated in the backend with values wrapped in array brackets.
        // That's wrong, but to make things backwards compatible until that's fixed, this code removes those brackets.
        const stringifiedArray = /^\['(.*)?'\]$/;
        const match = String(val).match(stringifiedArray);
        return match?.[1] ? match?.[1] : val;
      })
      .map(
        val =>
          `${quote}${isString ? String(val).replace(/'/g, "''") : val}${quote}`,
      );

    const [prefix, suffix] = ((isMulti, columnType, operator) => {
      if (
        isMulti &&
        isArrayColumnType(columnType) &&
        (operator === 'IN' || operator === 'NOT IN')
      ) {
        return ['[', ']'];
      }
      if (isMulti) {
        return ['(', ')'];
      }
      return ['', ''];
    })(isMulti, columnType, operator);
    const comparatorsExpression =
      comparatorArray.length > 0
        ? `${prefix}${formattedComparators.join(', ')}${suffix}`
        : '';
    const columnName =
      typeof subject === 'object'
        ? (subject?.column_name ?? '')
        : (subject ?? '');

    // Special functions are used in expressions around array column types.
    // - For the 'IN' operator, a 'hasAny(columnName, [value1, value2])' expression is used.
    // - For other operators, an 'arrayExists(x -> x operator comparators, columnName)' expression is used.
    // Note: hasAny is used as a special case because it performs better than arrayExists (it can leverage database indices).
    //
    // For column types that aren't arrays, a 'columnName operator comparators' expression is used.
    if (
      isArrayColumnType(columnType) &&
      (operator === 'IN' || operator === 'NOT IN')
    ) {
      expression = `hasAny(${columnName}, ${comparatorsExpression})`;
    } else if (isArrayColumnType(columnType)) {
      expression = `arrayExists(x -> x ${operator} ${comparatorsExpression}, ${columnName})`;
    } else {
      expression = `${columnName} ${operator} ${comparatorsExpression}`;
    }
  }
  return expression;
};

// Get the filter SQL expression for one dimension
export const getCohortSqlValue = (
  operator,
  operatorSQL,
  dimensions,
  comparator,
  isKQL = false,
) => {
  const { column_name, column_type } = dimensions[0];
  let prefix = '';
  let suffix = '';

  if (
    [
      'CONTAINS',
      'CONTAINS_SENSITIVE',
      'NOT_CONTAINS',
      'NOT_CONTAINS_SENSITIVE',
    ].includes(operator)
  ) {
    prefix = '%';
    suffix = '%';
  } else if (['ENDS_WITH', 'NOT_ENDS_WITH'].includes(operator)) {
    prefix = '%';
  } else if (['STARTS_WITH', 'NOT_STARTS_WITH'].includes(operator)) {
    suffix = '%';
  }
  return getSimpleSQLExpression(
    column_name,
    column_type,
    operatorSQL,
    comparator,
    prefix,
    suffix,
    isKQL,
  );
};

// Transform simple cohort SQL to aggregated format
export const transformToAggregatedCohortSQL = (
  originalSQL,
  aggregator,
  aggregatorColumn,
  aggregatorOperator,
  aggregatorComparator,
) => {
  if (
    !originalSQL ||
    typeof originalSQL !== 'string' ||
    originalSQL.trim() === ''
  ) {
    return originalSQL;
  }

  const parseSQLComponents = sql => {
    const selectMatch = sql.match(/SELECT\s+(.+?)\s+FROM/i);
    const fromMatch = sql.match(/FROM\s+([^\s]+)/i);
    const whereMatch = sql.match(/WHERE\s+(.+)$/i);

    if (!selectMatch || !fromMatch || !whereMatch) {
      return null;
    }

    return {
      actorColumn: selectMatch[1].trim(),
      tableName: fromMatch[1].trim(),
      whereClause: whereMatch[0].trim(),
    };
  };

  // Determine SQL format and extract the SQL to parse
  const inClauseMatch = originalSQL.match(/^(.+?)\s+IN\s+\((.+)\)$/i);
  const isInClauseFormat = !!inClauseMatch;
  const sqlToParse = isInClauseFormat ? inClauseMatch[2].trim() : originalSQL;
  const outerColumn = isInClauseFormat ? inClauseMatch[1].trim() : null;

  const parsed = parseSQLComponents(sqlToParse);
  if (!parsed) {
    return originalSQL;
  }

  const aggregatedQuery = `SELECT ${parsed.actorColumn} FROM (SELECT ${parsed.actorColumn},
    ${aggregator}(${aggregatorColumn}) AS "Cohort Aggregation"
   FROM ${parsed.tableName}
   ${parsed.whereClause}
   GROUP BY ${parsed.actorColumn}
   HAVING "Cohort Aggregation" ${aggregatorOperator} ${aggregatorComparator} SETTINGS distributed_product_mode='local')`;

  return isInClauseFormat
    ? `${outerColumn} IN (${aggregatedQuery})`
    : `(${aggregatedQuery})`;
};

// Map SQL/ClickHouse aggregator names to KQL functions. FIRST/LAST are
// time-dependent and resolved by sqlAggregatorToKql below — they're not in
// this map because their KQL target depends on whether a time column exists.
export const SQL_TO_KQL_AGGREGATORS = {
  COUNT: 'count',
  COUNT_DISTINCT: 'dcount',
  UNIQUE: 'dcount',
  SUM: 'sum',
  AVG: 'avg',
  MIN: 'min',
  MAX: 'max',
  PERCENTILE: 'percentile',
};

// Resolve a SQL aggregator name to its KQL equivalent. FIRST/LAST upgrade to
// arg_min/arg_max when a time column is known; otherwise they degrade to
// take_any (arbitrary non-null value) since KQL has no order-free FIRST/LAST.
export const sqlAggregatorToKql = (aggregator, hasTimeColumn) => {
  const upper = aggregator.toUpperCase();
  if (upper === 'FIRST') return hasTimeColumn ? 'arg_min' : 'take_any';
  if (upper === 'LAST') return hasTimeColumn ? 'arg_max' : 'take_any';
  return SQL_TO_KQL_AGGREGATORS[upper] || aggregator.toLowerCase();
};

// Per-KQL-aggregator behavior driving output-column naming and call shape.
// Kept as a single table so every special case is visible at a glance and
// adding a new aggregator means adding one row.
//
//   autoName: how KQL auto-names the summarize output column
//     'prefix' → "${fn}_${col}" (or "${fn}_" when col is missing or "1")
//                e.g. dcount(UserId) → "dcount_UserId", count() → "count_"
//     'column' → the column name itself
//                e.g. take_any(UserId) → "UserId"
//
//   call: how to build the aggregate call expression
//     'single'    → "${fn}(${col})"
//     'argMinMax' → "${fn}(${timeColumn}, ${col})"  (sortExpr, returnExpr)
//
// We rely on KQL's auto-naming rather than an explicit "name = expr"
// assignment because the SQL→KQL translator rewrites '=' to '==', which
// would produce invalid KQL.
export const KQL_AGGREGATORS = {
  count: { autoName: 'prefix', call: 'single' },
  dcount: { autoName: 'prefix', call: 'single' },
  sum: { autoName: 'prefix', call: 'single' },
  avg: { autoName: 'prefix', call: 'single' },
  min: { autoName: 'prefix', call: 'single' },
  max: { autoName: 'prefix', call: 'single' },
  percentile: { autoName: 'prefix', call: 'single' },
  take_any: { autoName: 'column', call: 'single' },
  arg_min: { autoName: 'column', call: 'argMinMax' },
  arg_max: { autoName: 'column', call: 'argMinMax' },
};

// Map operation strings to forms that survive the SQL→KQL translator. The
// translator converts '=' → '==', which would corrupt '!=' to '!=='. Use '<>'
// for not-equal (translator converts to '!=') and '=' for equal (translator
// converts to '==').
export const TRANSLATOR_SAFE_OPERATORS = {
  '==': '=',
  '!=': '<>',
  IN: 'IN',
  'NOT IN': 'NOT IN',
  CONTAINS: 'CONTAINS',
  CONTAINS_SENSITIVE: 'CONTAINS_SENSITIVE',
};

// Transform KQL cohort expression to aggregated format.
// Uses KQL auto-generated column names (e.g. count_, dcount_col, col for take_any)
// to avoid the '=' assignment operator which the SQL→KQL translator corrupts.
// e.g. actor in (table | where ... | project actor)
// becomes: actor in (table | where ... | summarize agg(col) by actor | where agg_col op val | project actor)
export const transformToAggregatedCohortKQL = (
  originalKQL,
  aggregator,
  aggregatorColumn,
  aggregatorOperator,
  aggregatorComparator,
  timeColumn,
) => {
  if (
    !originalKQL ||
    typeof originalKQL !== 'string' ||
    originalKQL.trim() === ''
  ) {
    return originalKQL;
  }

  const projectIndex = originalKQL.lastIndexOf('| project');
  if (projectIndex === -1) {
    return originalKQL;
  }

  // Extract the actor column from "| project actor)" — the expression
  // ends with the closing paren of "actor in (...)"
  const projectPart = originalKQL.slice(projectIndex);
  const actorMatch = projectPart.match(/\| project\s+(\w+)\s*\)\s*$/);
  if (!actorMatch) {
    return originalKQL;
  }
  const actorColumn = actorMatch[1];

  const kqlAggregator = sqlAggregatorToKql(aggregator, !!timeColumn);
  const behavior = KQL_AGGREGATORS[kqlAggregator] || {
    autoName: 'prefix',
    call: 'single',
  };

  let autoColumnName;
  if (!aggregatorColumn || aggregatorColumn === '1') {
    autoColumnName = `${kqlAggregator}_`;
  } else if (behavior.autoName === 'prefix') {
    autoColumnName = `${kqlAggregator}_${aggregatorColumn}`;
  } else {
    autoColumnName = aggregatorColumn;
  }

  const kqlOperator =
    TRANSLATOR_SAFE_OPERATORS[aggregatorOperator] || aggregatorOperator;

  // Quote the comparator value for string comparisons in KQL.
  const kqlComparator = Number.isNaN(Number(aggregatorComparator))
    ? `'${aggregatorComparator}'`
    : aggregatorComparator;

  const aggCall =
    behavior.call === 'argMinMax'
      ? `${kqlAggregator}(${timeColumn}, ${aggregatorColumn})`
      : `${kqlAggregator}(${aggregatorColumn})`;
  const summarizeClause = `| summarize ${aggCall} by ${actorColumn} `;
  const havingClause = `| where ${autoColumnName} ${kqlOperator} ${kqlComparator} `;

  // Insert summarize + having before "| project actor)"
  return `${originalKQL.slice(0, projectIndex)}${summarizeClause}${havingClause}${originalKQL.slice(projectIndex)}`;
};

export const getCohortSQLExpression = (
  cohort,
  operator,
  operatorSQL,
  topSeriesStr,
  comparator,
  aggregator,
  aggregatorColumn,
  aggregatorOperator,
  aggregatorComparator,
  isKQL = false,
) => {
  const { dimensions } = cohort || {};
  let sql = cohort?.sql_expression_with_time_range;
  const dimensionCount = dimensions?.length || 0;

  if (
    !sql ||
    (!(operator && comparator) &&
      !(
        aggregator &&
        aggregatorColumn &&
        aggregatorOperator &&
        aggregatorComparator
      ))
  ) {
    return sql;
  }

  // Helper function to handle value formatting
  const formatValue = value =>
    typeof value === 'string' ? `'${value}'` : value;
  // Helper function to generate comparator SQL for multiple dimensions
  const generateInOperatorSQL = () => {
    const topSeries = topSeriesStr.map(item => JSON.parse(item));
    // Get sql multiple dimensions
    // For dimensions ['A', 'B'], will get dimension sql: "A", "B"
    const columnSql = dimensions.map(d => `"${d.column_name}"`).join(', ');
    // Get filter value sql
    // For topSeriesStr: [{"A": 1, "B": "test"}, {"A": 2, "B": "test2"}]
    // This formatSingleSeries will get each series sql values (1, 'test')
    const formatSingleSeries = series =>
      dimensions
        .map(({ column_name }) => `${formatValue(series[column_name])}`)
        .join(', ');
    // Get the combined series values.
    // For topSeriesStr: [{"A": 1, "B": "test"}, {"A": 2, "B": "test2"}]
    // Will get (1, 'test'), (2, 'test2')
    const values = topSeries
      .map(item => `(${formatSingleSeries(item, dimensions)})`)
      .join(', ');
    return `(${columnSql}) ${operatorSQL} (${values})`;
  };

  let comparatorSQL = '';
  if (sql && dimensionCount > 0) {
    // Multiple dimensions
    if (dimensionCount > 1 && topSeriesStr) {
      const comparatorArray = [];
      const joiner = isKQL ? ' and ' : ' AND ';
      // IN or NOT IN operator
      if (['IN', 'NOT IN'].includes(operator) && Array.isArray(topSeriesStr)) {
        comparatorArray.push(generateInOperatorSQL());
      }
      // == or != operator
      else if (
        !['IN', 'NOT IN'].includes(operator) &&
        typeof topSeriesStr === 'string'
      ) {
        const topSeries = JSON.parse(topSeriesStr);
        Object.entries(topSeries).forEach(([key, value]) => {
          comparatorArray.push(`"${key}" ${operatorSQL} ${formatValue(value)}`);
        });
      }
      comparatorSQL = comparatorArray.join(joiner);
    }

    // One dimension, support customized input
    else if (dimensionCount === 1 && comparator) {
      comparatorSQL = getCohortSqlValue(
        operator,
        operatorSQL,
        dimensions,
        comparator,
        isKQL,
      );
    }

    if (comparatorSQL !== '') {
      if (isKQL) {
        // KQL: insert "| where (comparator)" before "| project"
        const projectIndex = sql.lastIndexOf('| project');
        if (projectIndex !== -1) {
          sql = `${sql.slice(0, projectIndex)}| where (${comparatorSQL}) ${sql.slice(projectIndex)}`;
        }
      } else {
        // SQL: insert after "WHERE"
        const whereIndex = sql.indexOf('WHERE');
        if (whereIndex !== -1) {
          sql = `${sql.slice(0, whereIndex + 5)} (${comparatorSQL}) AND ${sql.slice(
            whereIndex + 6,
          )}`;
        }
      }
    }
  }

  if (
    sql &&
    aggregator &&
    aggregatorColumn &&
    aggregatorOperator &&
    aggregatorComparator
  ) {
    if (isKQL) {
      return transformToAggregatedCohortKQL(
        sql,
        aggregator,
        aggregatorColumn,
        aggregatorOperator,
        aggregatorComparator,
        cohort?.form_data?.granularity_sqla,
      );
    }
    // Transform the simple cohort SQL into an aggregated format
    return transformToAggregatedCohortSQL(
      sql,
      aggregator,
      aggregatorColumn,
      aggregatorOperator,
      aggregatorComparator,
      comparatorSQL,
    );
  }

  return sql;
};

export function formatSelectOptions(options) {
  return options.map(opt => [opt, opt.toString()]);
}

// Get the cohort label for display
export const getCohortLable = (
  cohortName,
  cohortOperator,
  cohortComparator,
  cohortAggregatorOperator,
  cohortAggregatorComparator,
) => {
  if (!cohortName) return '';

  if (cohortOperator && cohortComparator) {
    return `${cohortName} ${cohortOperator} ${cohortComparator}`;
  }
  if (cohortAggregatorComparator && cohortAggregatorOperator) {
    return `${cohortName} "Cohort Aggregation" ${cohortAggregatorOperator} ${cohortAggregatorComparator}`;
  }
  return cohortName;
};
